import { makeAutoObservable } from "mobx";
import FirebaseService from "./FirebaseService";
import { DocumentData, QueryDocumentSnapshot, collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, setDoc, startAfter, startAt, where } from "firebase/firestore";
import { getBlob, ref, uploadBytes } from "firebase/storage";
import { PolylineLayerOptions } from "@maptiler/sdk";
import { Slope } from "../types/slope";
import { Point } from "../types/visualization";
import { getSlopesAndElevationSeriesFromGeojsonFeature, getSectionsData } from "../utils/UnitsUtils";
import { getBoundsOfGeojson } from "../utils/getBoundsOfGeojson";
import { DebouncedFunc, debounce } from "lodash";
import { gpx } from "@tmcw/togeojson";
import { getSurfaceData, SurfaceData } from "../utils/getSurfaceData";

export type Section = {
    startDistance: number;
    endDistance: number;
};

export type ControlPoint = {
    id?: string;
    name?: string;
    type: "start" | "end" | "control";
    distance: number;
    wc?: boolean;
    food?: boolean;
    drinks?: boolean;
    crew?: boolean;
    dropbag?: boolean;
    cutOff?: Date;
};

export type SectionData = Omit<ControlPoint, 'type'> & {
    type: "start" | "end" | "control" | "section";
    startDistance: number;
    endDistance: number;
    distance: number;
    elevationGain?: number;
    elevationLoss?: number;
    minElevation: number;
    maxElevation: number;
    avgUphillGrade?: number;
    avgDownhillGrade?: number;
    beforeDistance: number;
    beforeElevationGain: number;
    beforeElevationLoss: number;
    afterDistance: number;
    afterElevationGain: number;
    afterElevationLoss: number;
};

export type PlannedEffort = {
    time: number;
    rpe: number;
    percentage?: number;  // % diff from realistic (pessimistic: slower, optimistic: faster)
    locked?: boolean;     // auto-sync with realistic when true
};

export type NutritionProductPlan = {
    id?: string;
    productId: string;
    quantity: number;
};

export type PlannedNutrition = {
    products?: NutritionProductPlan[];
};

export type GpxMetadata = {
    date: Date;
    name: string;
};

export type Plan = {
    id: string,
    name: string;
    date: Date;
    gpxFile: GpxMetadata;
    createdAt: Date;
    modifiedAt: Date;
    createdBy: string;
    distance: number;
    elevationGain: number;
    elevationLoss: number;
    sections: Section[];
    controlPoints: ControlPoint[];
    plannedEffort: PlannedEffort[];
    pessimisticEffort?: PlannedEffort[];
    optimisticEffort?: PlannedEffort[];
    plannedNutrition: PlannedNutrition[];
    notes: string[];
    type: "race" | "own",
    sport?: "run" | "bike";
    userId: string;
    raceId?: string;
    svg?: string;
    surfaceData?: SurfaceData[];
    comparisonId?: string; // Added property to reference a comparison
};

export default class PlansService {

    get plans() {
        return this._plans;
    }

    get hasMorePlans() {
        return this._hasMorePlans;
    }

    get isLoadingPlans() {
        return this._isLoadingPlans;
    }

    get loadedForUserId() {
        return this._loadedForUserId;
    }

    get isSavingPlan() {
        return this._isSavingPlan;
    }

    get hasSaveError() {
        return this._hasSaveError;
    }

    get plan() {
        return this._plan;
    }

    get elevationSeries() {
        return this._elevationSeries;
    }

    get distancePoints() {
        return this._distancePoints;
    }

    get sectionsData() {
        return this._sectionsData;
    }

    get slopes() {
        return this._slopes;
    }

    get currentSection() {
        return this._currentSection;
    }

    get polylinesSteep() {
        return this._polylinesSteep;
    }

    get polylinesSlope() {
        return this._polylinesSlope;
    }

    get polylinesFull() {
        return this._polylinesFull;
    }

    get bbox() {
        return this._bbox;
    }

    get latlng() {
        return this._latlng;
    }

    private _firebaseService: FirebaseService;

    private _plans: Plan[] = [];
    private _currentLatestPlan: QueryDocumentSnapshot<DocumentData, DocumentData> | null = null;
    private _hasMorePlans = true;
    private _loadedForUserId: string | null = null;

    private _isLoadingPlans = false;
    private _isSavingPlan = false;
    private _hasSaveError = false;

    private _plan: Plan | null = null;
    private _elevationSeries: Point[] = [];
    private _distancePoints: number[] = [];
    private _sectionsData: SectionData[] = [];
    private _slopes: Slope[] = [];
    private _currentSection: number = 0;
    private _polylinesSteep: PolylineLayerOptions[] = [];
    private _polylinesSlope: PolylineLayerOptions[] = [];
    private _polylinesFull: PolylineLayerOptions[] = [];
    private _bbox: [number, number, number, number] | undefined;
    private _latlng: [number, number][] = [];
    private _gpxFile: File | null = null;

    private readonly saveDelayHandler: DebouncedFunc<() => void>;

    constructor(firebaseService: FirebaseService) {

        this._firebaseService = firebaseService;

        this.saveDelayHandler = debounce(() => {
            if (this._plan) {
                this._isSavingPlan = true;
                this._hasSaveError = false;
                this.upsertPlan(this._plan.userId, this._plan).then((e) => {
                    this._isSavingPlan = false;
                }).catch((e) => {
                    this._isSavingPlan = false;
                    this._hasSaveError = true;
                    console.error(e);
                });

            }
        }, 1000, { leading: false, trailing: true });

        makeAutoObservable(this, undefined, { autoBind: true, deep: true })
    }

    async loadPlan(userId: string, planId: string): Promise<Plan | null> {

        const db = this._firebaseService.db;
        const docRef = doc(db, `plans/${userId}-${planId}`);
        return getDoc(docRef).then((docSnapshot) => {
            if (!docSnapshot.exists()) {
                return null;
            }
            const docSnapshotData = docSnapshot.data();
            const gpxMetadata = docSnapshotData.gpxFile && docSnapshotData.gpxFile.date ? { ...docSnapshotData.gpxFile, date: docSnapshotData.gpxFile.date.toDate() } : undefined;
            this._plan = {
                ...docSnapshotData,
                date: docSnapshotData.date.toDate(),
                gpxFile: gpxMetadata,
                plannedNutrition: docSnapshotData.plannedNutrition ? JSON.parse(docSnapshotData.plannedNutrition) : [],
                controlPoints: (docSnapshotData.controlPoints || []).map((cp: any) => ({ ...cp, cutOff: cp.cutOff ? cp.cutOff.toDate() : 0 }))
            } as Plan;
            return this._plan;
        }).catch(() => {
            return null;
        });
    };

    async loadPlans(userId: string, fromStart: boolean = false) {
        if (this._isLoadingPlans) {
            return;
        }

        if (fromStart) {
            this._currentLatestPlan = null;
            this._hasMorePlans = true;
            this._plans = [];
        }

        this._isLoadingPlans = true;
        const db = this._firebaseService.db;

        const collectionRef = collection(db, `plans`);
        const q = this._currentLatestPlan ? query(collectionRef, orderBy("date", "desc"), where('userId', '==', userId), limit(10), startAfter(this._currentLatestPlan)) :
            query(collectionRef, orderBy("date", "desc"), where('userId', '==', userId), limit(10));

        const querySnapshot = await getDocs(q);
        if (querySnapshot.docs.length < 10) {
            this._hasMorePlans = false;
        }
        if (querySnapshot.docs.length > 0) {
            this._currentLatestPlan = querySnapshot.docs[querySnapshot.docs.length - 1];
        }
        this._plans = [...this.plans, ...querySnapshot.docs.map(
            (docSnapshot) => {
                const planData = docSnapshot.data();
                return {
                    ...planData,
                    date: planData.date.toDate(),
                    plannedNutrition: planData.plannedNutrition ? JSON.parse(planData.plannedNutrition) : [],
                    controlPoints: (planData.controlPoints || []).map((cp: any) => ({ ...cp, cutOff: cp.cutOff ? cp.cutOff.toDate() : 0 }))
                } as Plan
            })];
        this._isLoadingPlans = false;
        this._loadedForUserId = userId;
    }

    async upsertPlan(userId: string, plan: Plan) {
        const db = this._firebaseService.db;
        const documentRef = doc(db, `plans/${userId}-${plan.id}`);

        const planToSave = { ...plan, plannedNutrition: JSON.stringify(plan.plannedNutrition || []) };

        // Keep in-memory list in sync so navigating back to Home shows fresh data
        const idx = this._plans.findIndex(p => p.id === plan.id);
        if (idx !== -1) {
            this._plans = [...this._plans.slice(0, idx), plan, ...this._plans.slice(idx + 1)];
        } else {
            this._plans = [plan, ...this._plans];
        }

        return setDoc(documentRef, planToSave);
    }

    async deletePlan(userId: string, planId: string) {
        const db = this._firebaseService.db;
        const documentRef = doc(db, `plans/${userId}-${planId}`);

        this._plans = this._plans.filter(p => p.id !== planId);

        return deleteDoc(documentRef);
    }

    async addGpxFile(bucket: string, file: File, name: string) {

        const storage = this._firebaseService.storage;
        const storageRef = ref(storage, `${bucket}/${name}.gpx`);

        return uploadBytes(storageRef, file);
    }

    async loadGpxFile(bucket: string, userId: string, planId: string) {

        const storage = this._firebaseService.storage;
        const storageRef = ref(storage, `${bucket}/${userId}-${planId}.gpx`);

        return getBlob(storageRef).then((gpxFile) => {
            return new File([gpxFile], `${userId}-${planId}`);
        }).catch(() => {
            return null;
        });
    }

    async loadGpxFileWithGpxName(gpxName: string, userId: string, planId: string) {

        const storage = this._firebaseService.storage;
        const storageRef = ref(storage, `${gpxName}.gpx`);

        return getBlob(storageRef).then((gpxFile) => {
            return new File([gpxFile], `${userId}-${planId}`);
        }).catch(() => {
            return null;
        });
    }

    async parseCurrentPlanGpxFile() {
        if (!this._plan) {
            return;
        }

        return this.loadGpxFile(`usersFiles/${this._plan.userId}`, this._plan.userId, this._plan.id).then((gpxFile) => {
            if (gpxFile) {
                this.handleFile(gpxFile);
                return true;
            }
            return false;
        });
    }

    setPlanData(plan: Plan, dontSave: boolean = false) {
        this._plan = plan;

        this._sectionsData = this._sectionsData.map((section) => {
            const controlPoint = plan.controlPoints.find((cp) => (section.type === "control" || section.type === "start" || section.type === "end") && cp.distance === section.distance);
            if (controlPoint) {
                return {
                    ...section,
                    name: controlPoint.name,
                    cutOff: controlPoint.cutOff,
                    food: controlPoint.food,
                    drinks: controlPoint.drinks,
                    wc: controlPoint.wc,
                    crew: controlPoint.crew,
                    dropbag: controlPoint.dropbag,
                }
            }
            return section;
        });

        if (!dontSave) {
            this.saveDelayHandler();
        }
    }

    async duplicatePlan(plan: Plan) {
        if (!this._gpxFile) {
            return;
        }
        const now = new Date();
        const planName = `${plan.name} (copy)`;
        const planId = `${planName.toLowerCase().replaceAll(" ", "-").replace(/[#?&%]/g, "")}-${now.getTime()}`;

        try {
            await this.addGpxFile(`usersFiles/${plan.userId}`, this._gpxFile, `${plan.userId}-${planId}`);
            const newPlan = { ...plan, id: planId, name: planName, createdAt: now, modifiedAt: now };
            newPlan.gpxFile = { ...(newPlan.gpxFile || {}), date: now };
            await this.upsertPlan(plan.userId, newPlan);
            this.clearPlan();
            return newPlan;
        } catch (error) {
            console.error("Error duplicating plan:", error);
            return null;
        }
    }

    clearPlan() {
        this._plan = null;
        this._elevationSeries = [];
        this._distancePoints = [];
        this._slopes = [];
        this._currentSection = 0;
        this._polylinesSteep = [];
        this._polylinesSlope = [];
        this._polylinesFull = [];
        this._bbox = undefined;
        this._latlng = [];
        this._gpxFile = null;
    }

    private handleFile(file: File) {
        const reader = new FileReader();
        reader.onload = async (e) => {
            const parser = new DOMParser();
            if (e?.target?.result) {
                const xmlDoc = parser.parseFromString(
                    e.target.result as string,
                    "text/xml"
                );
                const geoJson = gpx(xmlDoc);
                if (geoJson && geoJson.features && geoJson.features.length > 0) {
                    const { elevationSeries, distancePoints, altitudePoints, latlng } = await getSlopesAndElevationSeriesFromGeojsonFeature(geoJson.features[0]);

                    this._elevationSeries = elevationSeries;
                    this._sectionsData = getSectionsData(this._plan?.sections || [], this._plan?.controlPoints || [], distancePoints, altitudePoints);

                    this._distancePoints = distancePoints;
                    this._bbox = getBoundsOfGeojson(geoJson);

                    this._latlng = latlng;

                    if (!this._plan?.surfaceData?.length) {
                        getSurfaceData(latlng, distancePoints).then((surfaceData) => {
                            if (this._plan) {
                                this.setPlanData({ ...this._plan, surfaceData });
                            }
                        });
                    }
                }
            }
        };
        this._gpxFile = file;
        reader.readAsText(file);
    }
}